import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AuthService, AuthUser } from '@adminprodigydesign/axis';
import { EMPTY, fromEvent, merge, Subscription } from 'rxjs';
import {
  map,
  startWith,
  distinctUntilChanged,
  switchMap,
  filter,
  debounceTime,
} from 'rxjs/operators';

import {
  CheckoutService,
  OrderStatusResult,
  ReceiptOrder,
  UiOrderStatus,
} from '../../services/checkout.service';
import { ActivatedRoute, Router } from '@angular/router';

type OrderType = 'delivery' | 'collection';

type OrderStatus = UiOrderStatus;

type ApiOrderStatus =
  | 'pending'
  | 'confirmed'
  | 'cooking'
  | 'delivery'
  | 'completed'
  | 'cancelled';

type ApiPaymentStatus = 'payment_pre_auth' | 'paid' | 'refunded' | string;

interface ApiStatusResponse {
  status: 'success' | 'error';
  data?: {
    id: string;
    orderStatus: ApiOrderStatus;
    payment_status: ApiPaymentStatus;
    productTotalOriginalGross?: string;
    orderTotalCurrentGross?: string;
    orderTotalCurrentReason?: string;
  };
  message?: string;
}

interface StepDef {
  key: OrderStatus;
  title: string;
  hint?: string;
}

@Component({
  selector: 'app-thank-you-page',
  standalone: false,
  templateUrl: './thank-you-page.component.html',
  styleUrl: './thank-you-page.component.scss',
})
export class ThankYouPageComponent implements OnInit {
  orderId!: number;
  lastOrderCode!: string | null;
  clientEmail!: string;
  currentUser: AuthUser | null = null;
  guestId?: number;
  orderType?: OrderType;

  private bootstrappedFrom: 'url' | 'storage' | 'unknown' = 'unknown';

  orderNumber = ''; // display only
  publicOrderCode: string = '';
  orderStatus: OrderStatus = 'PLACED';

  // Example “rough times” (minutes):
  prepMins = { min: 10, max: 20 };
  deliveryMins = { min: 20, max: 30 };

  // ETA window
  etaFrom?: Date;
  etaTo?: Date;
  etaCountdown = '';

  loading = false;
  errorMsg = '';
  lastUpdated?: Date;

  isCancelled = false;
  cancelPaymentStatus?: ApiPaymentStatus;
  cancelReason?: string;
  cancelTotalOriginal?: string;
  cancelTotalModified?: string;

  showReceipt = false;
  receiptLoading = false;
  receiptError = '';
  receiptOrder?: ReceiptOrder;

  isRefunded = false;
  refundReason?: string;
  refundTotalOriginal?: string;
  refundTotalModified?: string;

  private timer?: any;
  private pollSub?: Subscription;
  private activitySub?: Subscription;

  @ViewChild('receiptSheet') receiptSheet?: ElementRef<HTMLDivElement>;

  private readonly stepOrder: OrderStatus[] = [
    'PLACED',
    'CONFIRMED',
    'PREPARING',
    'OUT_FOR_DELIVERY',
    'DELIVERED',
  ];

  private readonly deliveryStepsView: StepDef[] = [
    { key: 'PLACED', title: 'Order placed', hint: 'We’ve got your order' },
    {
      key: 'CONFIRMED',
      title: 'Restaurant Confirmed',
      hint: 'Your order has been accepted',
    },
    {
      key: 'PREPARING',
      title: 'Preparing your order',
      hint: 'Starting soon',
    },
    {
      key: 'OUT_FOR_DELIVERY',
      title: 'Out for delivery',
      hint: 'Driver is on the way',
    },
    {
      key: 'DELIVERED',
      title: 'Delivered',
      hint: '',
    },
  ];

  private readonly collectionStepsView: StepDef[] = [
    { key: 'PLACED', title: 'Order placed', hint: 'We’ve got your order' },
    {
      key: 'CONFIRMED',
      title: 'Restaurant Confirmed',
      hint: 'Your order has been accepted',
    },
    {
      key: 'PREPARING',
      title: 'Preparing your order',
      hint: 'Starting soon',
    },
    {
      key: 'OUT_FOR_DELIVERY',
      title: 'Ready for collection',
      hint: 'You can pick it up',
    },
    {
      key: 'DELIVERED',
      title: 'Completed',
      hint: '',
    },
  ];

  get stepsView(): StepDef[] {
    return this.orderType === 'collection'
      ? this.collectionStepsView
      : this.deliveryStepsView;
  }

  get currentStepIndex(): number {
    return Math.max(
      this.stepsView.findIndex((s) => s.key === this.orderStatus),
      0,
    );
  }

  get refundCopy(): { title: string; body: string } {
    return {
      title: 'Your order was refunded',
      body:
        'A full refund has been issued to your original payment method. ' +
        'Depending on your bank, it may take 3–5 working days to appear.',
    };
  }

  constructor(
    private checkout: CheckoutService,
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthService,
  ) {}

  ngOnInit(): void {
    this.auth.user$.subscribe((user) => {
      this.currentUser = user;
    });

    // A) Try to hydrate from URL first
    const qp = this.route.snapshot.queryParamMap;

    const idFromUrl = Number(qp.get('orderId') ?? NaN);
    const emailFromUrl = qp.get('email') ?? '';
    const guestIdFromUrl = Number(qp.get('guestId') ?? NaN); // optional, if you add it later

    if (Number.isFinite(idFromUrl) && idFromUrl > 0) {
      this.orderId = idFromUrl;
      sessionStorage.setItem('lastOrderId', String(idFromUrl));
      this.bootstrappedFrom = 'url';
    } else {
      // B) Fallback to storage
      const idFromStorage = this.resolveOrderIdFromStorage();
      if (idFromStorage) {
        this.orderId = idFromStorage;
        this.bootstrappedFrom = 'storage';
        this.lastOrderCode = sessionStorage.getItem('lastOrderCode');
      }
    }

    // Email: prefer URL, else storage, else empty
    if (emailFromUrl) {
      this.clientEmail = emailFromUrl;
      sessionStorage.setItem('clientEmail', emailFromUrl);
    } else {
      this.clientEmail = sessionStorage.getItem('clientEmail') || '';
    }

    // guestId: if provided in URL, persist to localStorage (optional)
    if (Number.isFinite(guestIdFromUrl) && guestIdFromUrl > 0) {
      localStorage.setItem('guestId', String(guestIdFromUrl));
      this.guestId = guestIdFromUrl;
    } else {
      this.guestId = this.resolveGuestId();
    }

    // Guard: if we still don’t have an orderId, show error and stop
    if (!this.orderId) {
      this.errorMsg =
        'We could not find your order. Please use your email link again or place a new order.';
      return;
    }

    // Continue as before
    this.computeEta();
    this.startCountdown();

    this.onRefresh(); // initial fetch
    this.setupActivityGatedPolling(); // controlled polling
  }

  ngOnDestroy(): void {
    this.stopPolling(true); // final cleanup
    this.activitySub?.unsubscribe();
    this.activitySub = undefined;
  }

  // Manual refresh
  onRefresh() {
    this.loading = true;
    this.checkout.getOrderStatus(this.orderId, this.guestId).subscribe({
      next: (res) => this.handleAnyStatus(res),
      error: () => (this.errorMsg = 'Unable to refresh order status.'),
      complete: () => (this.loading = false),
    });
  }

  viewReceipt() {
    if (!this.orderId || !this.clientEmail) return;
    this.receiptLoading = true;
    this.receiptError = '';
    this.checkout.viewReceipt(this.orderId, this.clientEmail).subscribe({
      next: (r) => {
        this.receiptLoading = false;
        if (r.status === 'success' && r.order) {
          this.receiptOrder = r.order;
          this.showReceipt = true;
        } else {
          this.receiptError = r.message || 'Could not load receipt.';
        }
      },
      error: () => {
        this.receiptLoading = false;
        this.receiptError = 'Could not load receipt.';
      },
    });
  }

  closeReceipt() {
    this.showReceipt = false;
  }

  printReceipt() {
    const el = this.receiptSheet?.nativeElement;
    if (!el) return;

    // Inline, minimal styles so the print matches your modal layout
    const css = `
    <style>
      @page { size: auto; margin: 12mm; }
      html, body { padding: 0; margin: 0; }
      body { 
        font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; 
        color: #111827;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      :root { --border:#e5e7eb; --muted:#6b7280; }
      .mono { font-variant-numeric: tabular-nums; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
      /* Receipt layout (mirror your SCSS, but remove modal constraints) */
      .receipt-sheet { width: 100%; max-width: 860px; margin: 0 auto; box-shadow: none; border-radius: 0; padding: 0; }
      .receipt-header { display:flex; align-items:center; justify-content:space-between; gap:1rem; margin-bottom: .25rem; }
      .brand-sub { color: var(--muted); }
      .meta-grid { display:grid; gap:.25rem; grid-template-columns: repeat(auto-fit, minmax(220px,1fr)); text-align:left; margin:.5rem 0; }
      .addr { text-align:left; background:#f9fafb; border:1px solid var(--border); border-radius:12px; padding:.75rem 1rem; }
      table.items { width:100%; border-collapse:collapse; font-variant-numeric: tabular-nums; margin-top:.5rem; }
      table.items th, table.items td { padding:.6rem .5rem; border-bottom:1px solid #e5e7eb; text-align:center; }
      table.items th.left, table.items td.left { text-align:left; }
      table.items td.right { text-align:right; }
      .item-name { font-weight:600; }
      .item-note { color: var(--muted); font-size:.85rem; }
      .extras { margin:.25rem 0 0; padding-left:1rem; color:#374151; }
      tfoot .subtotal td { font-weight:600; }
      tfoot .total    td { font-weight:800; }
      .print-footer { text-align:center; color: var(--muted); margin-top:.5rem; }
    </style>
  `;

    const html = `
    <!doctype html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>Receipt #${this.receiptOrder?.id ?? ''}</title>
        ${css}
      </head>
      <body>${el.outerHTML}</body>
    </html>
  `;

    const w = window.open('', '_blank', 'noopener,noreferrer');
    if (!w) {
      window.print();
      return;
    } // very rare fallback
    w.document.open();
    w.document.write(html);
    w.document.close();
    w.focus();
    w.onload = () => {
      w.print();
      w.close();
    };
  }

  get computedSubtotal(): number {
    if (!this.receiptOrder) return 0;
    return this.receiptOrder.details.reduce((sum, it) => {
      const lineTotal = this.price(it.itemTotalGrossOriginal);
      const qty = Number(it.itemQuantity ?? 0);

      if (!qty || qty <= 0) return lineTotal; // safe fallback
      return lineTotal / qty;
    }, 0);
  }

  // ——— Internals ———

  // Map backend -> UI stepper status
  private mapApiToUiStatus(api: ApiOrderStatus): OrderStatus {
    switch (api) {
      case 'pending':
        return 'PLACED';
      case 'confirmed':
        return 'CONFIRMED';
      case 'cooking':
        return 'PREPARING';
      case 'delivery':
        return 'OUT_FOR_DELIVERY';
      case 'completed':
        return 'DELIVERED';
      default:
        return 'PLACED';
    }
  }

  // Only allow forward progress (ignore duplicates/out-of-order)
  private progressGuard(next: OrderStatus): OrderStatus {
    const currIdx = this.stepOrder.indexOf(this.orderStatus);
    const nextIdx = this.stepOrder.indexOf(next);
    if (nextIdx === -1) return this.orderStatus;
    return nextIdx >= currIdx ? next : this.orderStatus;
  }

  private stopPolling(final = false) {
    if (this.pollSub) {
      this.pollSub.unsubscribe();
      this.pollSub = undefined;
    }
    if (final && this.timer) {
      clearInterval(this.timer);
      this.timer = undefined;
      this.etaCountdown = 'Arrived';
    }
  }

  private setStatus(status: OrderStatus) {
    this.orderStatus = status;
  }

  private resolveOrderIdFromStorage(): number | undefined {
    const raw = sessionStorage.getItem('lastOrderId');
    const id = raw ? Number(raw) : NaN;
    return Number.isFinite(id) && id > 0 ? id : undefined;
  }

  private resolveGuestId(): number | undefined {
    const g = Number(localStorage.getItem('guestId') ?? NaN);
    return Number.isFinite(g) && g > 0 ? g : undefined;
  }

  private computeEta() {
    const now = new Date();
    const minTotal = this.prepMins.min + this.deliveryMins.min;
    const maxTotal = this.prepMins.max + this.deliveryMins.max;
    this.etaFrom = new Date(now.getTime() + minTotal * 60_000);
    this.etaTo = new Date(now.getTime() + maxTotal * 60_000);
  }

  private startCountdown() {
    this.updateCountdown();
    this.timer = setInterval(() => this.updateCountdown(), 15_000);
  }

  private updateCountdown() {
    if (!this.etaTo) return;
    const ms = this.etaTo.getTime() - Date.now();
    if (ms <= 0) {
      this.etaCountdown = 'Arriving now';
      return;
    }
    const mins = Math.ceil(ms / 60_000);
    this.etaCountdown =
      mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins} min`;
  }

  formatTime(d: Date): string {
    const h = d.getHours(),
      m = d.getMinutes();
    const h12 = ((h + 11) % 12) + 1;
    const ampm = h < 12 ? 'AM' : 'PM';
    const mm = m < 10 ? `0${m}` : m;
    return `${h12}:${mm} ${ampm}`;
  }

  // Centralised handler that accepts either OrderStatusResult or ApiStatusResponse
  private handleAnyStatus(res: OrderStatusResult) {
    this.publicOrderCode = res.publicOrderCode;
    this.orderType = (res.orderType || '').toLowerCase() as
      | OrderType
      | undefined;

    // REFUNDED
    if ((res.paymentStatus ?? '').toLowerCase() === 'refunded') {
      this.isRefunded = true;
      this.isCancelled = false;
      this.refundReason = res.refund?.reason;
      this.refundTotalOriginal = res.refund?.totalOriginal;
      this.refundTotalModified = res.refund?.totalModified;

      this.stopPolling(true);
      this.etaFrom = this.etaTo = undefined;
      this.etaCountdown = '';
      this.lastUpdated = new Date();
      this.errorMsg = '';
      return;
    }

    // CANCELLED
    if (res.uiStatus === 'CANCELLED') {
      this.isCancelled = true;
      this.cancelPaymentStatus = res.cancel?.paymentStatus as any;
      this.cancelReason = 'cancelled by restaurant';
      this.cancelTotalOriginal = res.cancel?.totalOriginal ?? undefined;
      this.cancelTotalModified = res.cancel?.totalModified ?? undefined;

      this.stopPolling(true);
      this.etaFrom = this.etaTo = undefined;
      this.etaCountdown = '';
      this.lastUpdated = new Date();
      this.errorMsg = '';
      return;
    }

    // NORMAL FLOW
    const progressed = this.progressGuard(res.uiStatus);
    if (progressed !== this.orderStatus) {
      this.setStatus(progressed);
    }

    // compute ETA anchored to backend timestamps per phase
    if (this.finalised) {
      this.stopPolling(true);
    } else {
      this.recomputeEtaFromBackend(res);
    }

    this.lastUpdated = new Date();
    this.errorMsg = '';
    if (progressed === 'DELIVERED') this.stopPolling(true);
  }

  private setupActivityGatedPolling() {
    const computeActive = () =>
      typeof document !== 'undefined' &&
      typeof window !== 'undefined' &&
      document.visibilityState === 'visible' &&
      document.hasFocus();

    const visibility$ = fromEvent(document, 'visibilitychange').pipe(
      map(() => computeActive()),
      startWith(computeActive()),
    );
    const focus$ = merge(
      fromEvent(window, 'focus'),
      fromEvent(window, 'blur'),
    ).pipe(map(() => computeActive()));

    const activity$ = merge(visibility$, focus$).pipe(distinctUntilChanged());

    this.activitySub?.unsubscribe();
    this.activitySub = activity$
      .pipe(
        filter(() => !this.finalised), // ⬅️ don’t restart when refunded/cancelled/delivered
        switchMap((active) => {
          if (!active) {
            this.stopPolling(false);
            return EMPTY;
          }
          this.startPolling();
          return EMPTY;
        }),
      )
      .subscribe();
  }

  private startPolling() {
    if (this.pollSub || this.finalised) return; // ⬅️ guard here too
    this.pollSub = this.checkout
      .watchOrderStatus(this.orderId, this.guestId, 15000)
      .subscribe({
        next: (res) => this.handleAnyStatus(res),
        error: () => (this.errorMsg = 'Could not check status. Will retry…'),
      });
  }

  get cancelCopy(): { title: string; body: string } {
    if (!this.isCancelled) return { title: '', body: '' };
    const unpaid = this.cancelPaymentStatus === 'unpaid';
    const title = 'Your order was cancelled';
    const body = unpaid
      ? 'No payment was taken. If you see a pending card authorisation, it should drop off automatically.'
      : 'Payment was captured and a refund will be processed shortly.';
    return { title, body };
  }

  // Inside ThankYouPageComponent
  price(val: unknown): number {
    const n = Number(val ?? 0);
    return Number.isNaN(n) ? 0 : n;
  }

  lineTotal(it: {
    itemTotalGrossOriginal?: string | number;
    itemQuantity?: number;
  }): number {
    const price = this.price(it.itemTotalGrossOriginal);
    const qty = it.itemQuantity ?? 0;
    return price * qty;
  }

  get finalised(): boolean {
    return (
      this.isRefunded || this.isCancelled || this.orderStatus === 'DELIVERED'
    );
  }

  // ⬇️ Add these utility methods inside ThankYouPageComponent
  /** Parse "YYYY-MM-DD HH:mm:ss" (no TZ) as local time; fall back to ISO. */
  private parseBackendLocalOrIso(val?: string): Date | undefined {
    if (!val) return undefined;
    // If it looks like "2025-09-29 18:58:00"
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(val)) {
      // Interpret as local time (no timezone)
      const isoish = val.replace(' ', 'T'); // "YYYY-MM-DDTHH:mm:ss"
      const d = new Date(isoish);
      return Number.isNaN(d.getTime()) ? undefined : d;
    }
    // Otherwise let Date parse (covers "...Z" / ISO)
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }

  /** Basic clamp helper for remaining mins so the window never goes negative. */
  private clampRemaining(anchor: Date, mins: number): number {
    const elapsedMin = Math.max(
      0,
      Math.floor((Date.now() - anchor.getTime()) / 60000),
    );
    return Math.max(0, mins - elapsedMin);
  }

  /** Centralised ETA computation using backend timestamps and current UI phase. */
  private recomputeEtaFromBackend(res: OrderStatusResult) {
    // Final states handled by caller (they clear ETA)
    const phase = res.uiStatus;

    // Pick anchor by phase
    let anchor: Date | undefined;
    if (phase === 'OUT_FOR_DELIVERY') {
      anchor =
        this.parseBackendLocalOrIso(res.updatedAtLocal) ??
        this.parseBackendLocalOrIso(res.updatedAt);
    } else if (phase === 'PREPARING') {
      anchor =
        this.parseBackendLocalOrIso(res.updatedAtLocal) ??
        this.parseBackendLocalOrIso(res.updatedAt) ??
        this.parseBackendLocalOrIso(res.createdAtLocal) ??
        this.parseBackendLocalOrIso(res.createdAt);
    } else {
      // PLACED / CONFIRMED (or unknown → be optimistic)
      anchor =
        this.parseBackendLocalOrIso(res.createdAtLocal) ??
        this.parseBackendLocalOrIso(res.createdAt) ??
        new Date();
    }

    if (!anchor) {
      // Fallback to old behaviour if we somehow lack a time anchor
      this.computeEta();
      return;
    }

    // Build min/max remaining based on phase
    let minRemain: number;
    let maxRemain: number;

    if (phase === 'OUT_FOR_DELIVERY') {
      minRemain = this.deliveryMins.min;
      maxRemain = this.deliveryMins.max;
    } else {
      // Treat the moment we switched into PREPARING as the start of prep, so
      // remaining = full prep + delivery (time since anchor will be discounted below)
      minRemain = this.prepMins.min + this.deliveryMins.min;
      maxRemain = this.prepMins.max + this.deliveryMins.max;
    }

    // Discount elapsed time since anchor so ETA tightens as time passes
    const minLeft = this.clampRemaining(anchor, minRemain);
    const maxLeft = this.clampRemaining(anchor, maxRemain);

    // If both collapsed to 0 (e.g., long delay), show a tiny forward window
    const safeMin = Math.max(minLeft, 1);
    const safeMax = Math.max(maxLeft, Math.max(safeMin + 4, 6)); // keep a sensible window

    const now = new Date();
    this.etaFrom = new Date(now.getTime() + safeMin * 60_000);
    this.etaTo = new Date(now.getTime() + safeMax * 60_000);
    this.updateCountdown();
  }

  get showCreateAccountPrompt(): boolean {
    return (
      !this.currentUser &&
      !this.isCancelled &&
      !this.isRefunded &&
      !!this.orderId
    );
  }

  goToRegister(): void {
    this.router.navigate(['/register'], {
      queryParams: {
        returnUrl: '/account',
        email: this.clientEmail || null,
      },
    });
  }

  goToLogin(): void {
    this.router.navigate(['/login'], {
      queryParams: {
        returnUrl: '/account',
        email: this.clientEmail || null,
      },
    });
  }

  get receiptSubtotal(): number {
    if (!this.receiptOrder?.details?.length) return 0;

    return this.receiptOrder.details.reduce((sum, it) => {
      return sum + this.price(it.itemTotalGrossOriginal);
    }, 0);
  }

  get receiptSmallOrderFee(): number {
    return this.price(this.receiptOrder?.smallOrderFeeGross);
  }

  get receiptDeliveryFee(): number {
    return this.price(this.receiptOrder?.deliveryFeeGross);
  }

  get receiptOrderFee(): number {
    return this.price(this.receiptOrder?.orderFeeOriginalGross);
  }

  get receiptTotal(): number {
    return this.price(this.receiptOrder?.orderTotalCurrentGross);
  }
}
